import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatSort, MatSortModule} from '@angular/material/sort';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatIconModule} from '@angular/material/icon';
import {MatDividerModule} from '@angular/material/divider';
import {MatBadgeModule} from '@angular/material/badge';
import {MatChipInputEvent, MatChipsModule} from '@angular/material/chips';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatMenuModule} from '@angular/material/menu';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatButtonModule} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatDialog} from '@angular/material/dialog';

import {CdkDragDrop, DragDropModule, moveItemInArray} from '@angular/cdk/drag-drop';
import {COMMA, ENTER} from '@angular/cdk/keycodes';

import {CommonModule} from '@angular/common';

import {CrudDataTableService} from './crud-data-table.service';
import {
  CrudTableSelectColsDialogComponent
} from './crud-table-select-cols-dialog/crud-table-select-cols-dialog.component';

import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';
import autoTable from 'jspdf-autotable';
import jsPDF from 'jspdf';
import {PluckPipe} from "projects/nter-ui-kit/src/lib/webkit/pipes/pluck.pipe";
import {MatInputModule} from "@angular/material/input";
import {
  generateDefaultSearchTerms,
  generateTableColumns, identifyDefaultSort
} from "projects/nter-ui-kit/src/lib/webkit/components/crud-item/crud-item-decorator";
import {MatProgressBar} from "@angular/material/progress-bar";
import {BehaviorSubject, Observable, Subject, Subscription, takeUntil} from "rxjs";
import {SnackbarService} from "projects/nter-ui-kit/src/lib/framework/util/service/snackbar.service";
import {
  AppTableDisplayColumn,
  TableActionItem
} from "projects/nter-ui-kit/src/lib/webkit/components/crud-table/crud-table.model";
import {PanelType} from "@ui-kit/webkit/components/info-panel/info-panel.component";
import {ExpandingInfoPanelComponent} from "../expanding-info-panel/expanding-info-panel.component";

@Component({
  selector: 'app-crud-table',
  standalone: true,
  imports: [
    CommonModule,
    PluckPipe,
    DragDropModule,
    MatBadgeModule,
    MatButtonModule,
    MatChipsModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatMenuModule,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatToolbarModule,
    MatTooltipModule,
    MatInputModule,
    MatProgressBar,
    ExpandingInfoPanelComponent,
  ],
  templateUrl: './crud-table.component.html',
  styleUrls: ['./crud-table.component.scss'],
})
export class CrudTableComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @Input() title?: string | null = null;
  @Input() subTitle?: string | null = null;
  @Input() longDescription?: string | null = null;
  @Input() filterType: 'MATERIAL' | 'NATIVE' | 'NONE' = 'MATERIAL';
  @Input() displayColumns: AppTableDisplayColumn[] = [];
  @Input() modelType?: any;
  @Input() apiEndpointRoot?: string;
  @Input() additionalActions: TableActionItem[] = [];
  @Input() interruptPendingChanges: boolean = false;
  @Input() tableData?: any[];

  @Input() showColumns: string[] = [];
  @Output() showColumnsChange = new EventEmitter<string[]>();

  private _loadingSubject = new BehaviorSubject<boolean>(false);
  /**
   * Optional loading observable to show loading spinner when data is being fetched. If not provided, the component will
   * be initialized to false, effectively hiding it from view.
   * @type {Observable<boolean>}
   */
  @Input() loading$: Observable<boolean> = this._loadingSubject.asObservable();
  @Input() showLoaderBars = true;

  @Input() updatedRow: any;
  @Input() unselectRow: boolean = false;
  @Output() rowSelected = new EventEmitter<any>();

  @Input() showLoadMore: boolean = false;
  @Input() showReload: boolean = true;
  @Input() maxPageSize: number = 10;
  @Output() reloadData = new EventEmitter<void>();
  @Output() loadData = new EventEmitter<void>();

  dataSource = new MatTableDataSource();
  searchTerms: string[] = [];

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('crudPaginator') paginator!: MatPaginator;

  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  protected selectedRow: any = null;
  protected loadDataSubscription: Subscription | null = null;
  private _defaultSearchTerms: string[] = [];

  private _clearOnDestroy$ = new Subject<void>();

  protected moreInfoMessage: boolean = false;
  protected showMoreInfoMessage: boolean = false;
  protected readonly PanelType = PanelType;

  constructor(
    private crudService: CrudDataTableService,
    public dialog: MatDialog,
    private _snackbarService: SnackbarService
  ) {
  }

  ngOnInit(): void {
    if (!this.displayColumns || this.displayColumns.length === 0) {
      this.displayColumns = generateTableColumns(this.modelType);
      if (this.showColumns && this.showColumns.length > 0) {
        this.displayColumns.forEach((col) => {
          col.visible = this.showColumns.includes(col.colName);
        })
      }
    }

    if (this.tableData && !this.apiEndpointRoot) {
      this.dataSource.data = this.tableData;
      this.clearSearchTerms();
    } else {
      this.loadDataSubscription = this.crudService
        .getAllData(this.apiEndpointRoot ?? '')
        .pipe(takeUntil(this._clearOnDestroy$))
        .subscribe((data: any[]) => {
          this.dataSource.data = data;
          this.clearSearchTerms();
        });
    }

    this._defaultSearchTerms = generateDefaultSearchTerms(this.modelType);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['unselectRow'] && changes['unselectRow'].currentValue === true && changes['unselectRow'].previousValue !== changes['unselectRow'].currentValue) {
      this.onRowClicked(null, true);
    }

    if (changes['updatedRow'] && !changes['updatedRow'].firstChange && changes['updatedRow'].currentValue) {
      this.updateRowData(changes['updatedRow'].currentValue);
    }

    if (changes['tableData'] && !changes['tableData'].firstChange && changes['tableData'].currentValue && !this.apiEndpointRoot) {
      this.dataSource.data = changes['tableData'].currentValue;
      this.clearSearchTerms();
    }

    if (changes['showColumns'] && !changes['showColumns'].firstChange && changes['showColumns'].currentValue && changes['showColumns'].currentValue.length > 0) {
      this.displayColumns.forEach((col) => {
        col.visible = this.showColumns.includes(col.colName);
      })
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.displayColumns && this.displayColumns.length != 0) {
        let defaultSort: MatSort | null = identifyDefaultSort(this.modelType);
        if (defaultSort) {
          this.sort.active = defaultSort.active;
          this.sort.direction = defaultSort.direction;
          this.sort.sortChange.emit(defaultSort);
        }
      }
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
    }, 0);
  }

  ngOnDestroy(): void {
    this._clearOnDestroy$.next();
  }

  get visibleColumns() {
    return this.displayColumns.filter((column) => column.visible);
  }

  applyNativeFilter(event: Event) {
    const input = event.target as HTMLInputElement;
    const filterValue = input.value;

    if (filterValue.trim()) {
      if (!this.searchTerms.includes(filterValue.trim())) {
        this.searchTerms.push(filterValue.trim());
      }

      input.value = '';
      this.applyAdvancedFilters();
    }
  }

  applyMaterialFilter(event: MatChipInputEvent) {
    const filterValue = (event.value || '').trim();

    if (filterValue) {
      if (!this.searchTerms.includes(filterValue.trim())) {
        this.searchTerms.push(filterValue.trim());
      }

      event.chipInput!.clear();
      this.applyAdvancedFilters();
    }
  }

  refreshData() {
    this.reloadData.emit();
  }

  loadMoreData() {
    this.loadData.emit();
  }

  get canLoadMoreData() {
    return this.tableData && this.tableData.length != 0 && (this.tableData.length % this.maxPageSize == 0);
  }

  removeSearchTerm(index: number) {
    this.searchTerms.splice(index, 1);
    this.applyAdvancedFilters();
  }

  clearSearchTerms() {
    this.searchTerms = [];
    this.dataSource.filter = '';

    if (this._defaultSearchTerms) {
      this.searchTerms.push(...this._defaultSearchTerms);
      this.applyAdvancedFilters();
    }
  }

  private matchRowFilterCriteria(
    key: string,
    value: any,
    columnValue: any
  ): boolean {
    switch (key) {
      case 'number.min':
        return parseFloat(columnValue) >= parseFloat(value);
      case 'number.max':
        return parseFloat(columnValue) <= parseFloat(value);
      case 'number.between':
        const [min, max] = value
          .split('and')
          .map((v: any) => parseFloat(v.trim()));
        return parseFloat(columnValue) >= min && parseFloat(columnValue) <= max;
      case 'date.after':
        return new Date(columnValue) > new Date(value);
      case 'date.before':
        return new Date(columnValue) < new Date(value);
      case 'date.between':
        const [start, end] = value
          .split('and')
          .map((v: any) => new Date(v.trim()));
        return new Date(columnValue) >= start && new Date(columnValue) <= end;
      default:
        return !(key.includes('number') || key.includes('date')); //default return true as no advanced filter was applicable
    }
  }

  private matchColumnFilterCriteria(
    searchColumn: string,
    operation: string,
    probe: any,
    data: any
  ): boolean {
    const column = this.visibleColumns.find(
      (col) =>
        col.displayLabel?.toLowerCase() === searchColumn?.toLowerCase() ||
        col.colName?.toLowerCase() === searchColumn?.toLowerCase()
    );
    if (!column) return false;
    const colValue = data[column.colName]?.toString() ?? null;
    switch (operation) {
      case 'exact':
        return colValue?.toLowerCase() === probe?.toString().toLowerCase();
      case 'in':
      case 'contains':
        const valuesArray = probe.split(',').map((v: string) => v.trim());
        return (
          valuesArray.includes(colValue) ||
          valuesArray.some((val: any) => colValue.includes(val))
        );
      default:
        return false;
    }
  }

  applyAdvancedFilters() {
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      const allTerms = JSON.parse(filter);
      const localSearchTerms = [...allTerms]; // Making a local copy

      // 1. Column filters: Terms starting with a column display label
      const colLabelPrefixes = this.visibleColumns.map((col) =>
        col.displayLabel.toLowerCase()
      );
      const columnFilters = localSearchTerms.filter((term) =>
        colLabelPrefixes.some(
          (prefix) =>
            typeof term === 'string' &&
            (term.toLowerCase().startsWith(prefix + '.') ||
              term.toLowerCase().startsWith(prefix + ':'))
        )
      );
      // Remove column filters from local search terms
      columnFilters.forEach((term) =>
        localSearchTerms.splice(localSearchTerms.indexOf(term), 1)
      );

      // 2. Row filters: Terms starting with "number." or "date."
      const rowFilters = localSearchTerms.filter(
        (term) =>
          typeof term === 'string' &&
          (term.startsWith('number.') || term.startsWith('date.'))
      );
      // Remove row filters from local search terms
      rowFilters.forEach((term) =>
        localSearchTerms.splice(localSearchTerms.indexOf(term), 1)
      );

      // Remaining terms are plain search terms
      const plainSearchTerms = localSearchTerms;

      // 4. Apply plain search
      const matchPlainSearch =
        plainSearchTerms.length === 0 ||
        plainSearchTerms.every((term) =>
          this.visibleColumns.some((column) => {
            const columnValue = data[column.colName]
              ? data[column.colName].toString().toLowerCase()
              : '';
            return columnValue.includes(term.toLowerCase());
          })
        );

      // 5. Apply row search
      const matchRowSearch =
        rowFilters.length === 0 ||
        rowFilters.every((term) => {
          const [key, value] = term.split(':').map((part: any) => part.trim());
          return this.visibleColumns.some((column) => {
            const columnValue = data[column.colName]
              ? data[column.colName].toString()
              : '';
            return this.matchRowFilterCriteria(key, value, columnValue);
          });
        });

      // 6. Apply column search
      const matchColumnSearch =
        columnFilters.length === 0 ||
        columnFilters.every((term) => {
          const [fullKey, probe] = term
            .split(':')
            .map((part: any) => part.trim());
          const [searchColumn, operation = 'exact'] = fullKey.includes('.')
            ? fullKey.split('.')
            : [fullKey];

          return this.visibleColumns.some((column) => {
            if (
              (column.displayLabel.toLowerCase() ===
                searchColumn.toLowerCase() ||
                column.colName.toLowerCase() === searchColumn.toLowerCase()) &&
              data[column.colName]
            ) {
              return this.matchColumnFilterCriteria(
                column.displayLabel,
                operation,
                probe,
                data
              );
            }
            return false;
          });
        });
      return matchPlainSearch && matchRowSearch && matchColumnSearch;
    };

    this.dataSource.filter = JSON.stringify(this.searchTerms);
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(
      this.displayColumns,
      event.previousIndex,
      event.currentIndex
    );
    this.displayColumns = [...this.displayColumns];
  }

  openColsDialog() {
    const dialogRef = this.dialog.open(CrudTableSelectColsDialogComponent, {
      restoreFocus: false,
      data: {displayColumns: this.displayColumns},
    });

    dialogRef.componentInstance.displayColumnsChange.subscribe(
      (updatedColumns: AppTableDisplayColumn[]) => {
        this.displayColumns = updatedColumns;
        this.showColumns = this.displayColumns.map((col) => col.colName);
        this.showColumnsChange.emit(this.showColumns);
      }
    );
  }

  exportToCsv() {
    const dataWithDisplayLabels = this.dataSource.filteredData.map(
      (item: any) => {
        const newItem: any = {};
        this.visibleColumns.forEach((column) => {
          let exportName = column.exportLabel ? column.exportLabel : column.displayLabel.toUpperCase();
          newItem[exportName] = column.exportFunction ? column.exportFunction(item[column.colName]) : item[column.colName];
        });
        return newItem;
      }
    );

    const csv = Papa.unparse(dataWithDisplayLabels);
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    this.downloadFile(url, `${this.title}-${new Date().toISOString()}.csv`);
  }

  exportToExcel() {
    const dataWithDisplayLabels = this.dataSource.filteredData.map(
      (item: any) => {
        const newItem: any = {};
        this.visibleColumns.forEach((column) => {
          let exportName = column.exportLabel ? column.exportLabel : column.displayLabel.toUpperCase();
          newItem[exportName] = column.exportFunction ? column.exportFunction(item[column.colName]) : item[column.colName];
        });
        return newItem;
      }
    );

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataWithDisplayLabels);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, `${this.title}-${new Date().toISOString()}.xlsx`);
  }

  exportToPdf() {
    const doc = new jsPDF();
    const columns = this.visibleColumns.map((col) => col.exportLabel ? col.exportLabel : col.displayLabel);
    const body = this.dataSource.filteredData.map((row: any) =>
      this.visibleColumns.map((col) => col.exportFunction ? col.exportFunction(row[col.colName]) : row[col.colName]?.toString() || '')
    );
    autoTable(doc, {
      head: [columns],
      body: body,
    });
    doc.save(`${this.title}-${new Date().toISOString()}.pdf`);
  }

  private downloadFile(url: string, fileName: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  /**
   * Will emit the selected row if the row is not already selected. If the row is already selected, it will emit null.
   * If the called has provided an impl for pendingChangesInterruptHandler, it will be called and if it returns true,
   * the function will return early, not allowing the user to perform further actions.
   * @param row
   */
  onRowClicked(row: any): void;
  onRowClicked(row: any, skipEmit: boolean): void;
  onRowClicked(row: any, skipEmit: boolean = false): void {
    // if (this.selectedRow)
    if (this.interruptPendingChanges) {
      this._snackbarService.openSnackBar("There are pending changes, please save or discard before closing");
      return;
    }

    this.selectedRow = this.selectedRow === row ? null : row;
    if (!skipEmit)
      this.rowSelected.emit(this.selectedRow);
  }

  updateRowData(updatedRow: any) {
    const idColumn = this.displayColumns.find(
      (column) => column.identifier
    )?.colName;

    if (!idColumn) {
      console.error('Identifier column not set.');
      return;
    }

    const data = this.dataSource.data;
    const index = data.findIndex(
      (item: any) => item[idColumn] === updatedRow[idColumn]
    );
    if (index !== -1) {
      data[index] = updatedRow;
      this.dataSource.data = data;
    } else {
      this.dataSource.data = [...this.dataSource.data, updatedRow];
    }

    this.reApplyFilters();
    this.onRowClicked(updatedRow);
  }

  reApplyFilters() {
    this.dataSource.filter = this.dataSource.filter;
    const paginatorIndex = this.paginator.pageIndex;
    const pageIndex =
      paginatorIndex < this.paginator.getNumberOfPages()
        ? paginatorIndex
        : this.paginator.getNumberOfPages() - 1;
    this.paginator.pageIndex = pageIndex;
    this.paginator._changePageSize(this.paginator.pageSize);
  }

  /**
   * Return true if the current search terms are different from the default search terms. This is so that on a clear
   * the default search terms are applied and don't show the user the clear button again. User must manually clear
   * default searches
   * @protected
   */
  protected hasNonDefaultSearchTerms() {
    return ((this.searchTerms?.length ?? 0) > 0 && this.searchTerms.length !== this._defaultSearchTerms.length)
      || ((this._defaultSearchTerms?.length ?? 0) > (this.searchTerms?.length ?? 0));
  }

  protected onAdditionalActionSelect(tableActionItem: TableActionItem): void {
    switch (tableActionItem.actionDataSource) {
      case 'SelectedRow':
        tableActionItem.supplier = (): any => {
          return this.selectedRow
        };
        tableActionItem.enabled = 'RowSelected';
        tableActionItem.keepSelectedRowOnClick = tableActionItem.keepSelectedRowOnClick ?? true;
        break;
      case 'AllRows':
        tableActionItem.supplier = (): any => {
          return this.dataSource.filteredData
        };
        break;
      case 'EmptyRow':
        tableActionItem.supplier = (() => {
          return new this.modelType()
        });
        break;
      case 'CustomSupplier':
        tableActionItem.supplier = tableActionItem.supplier ?? (() => null);
        break;
      default:
        tableActionItem.supplier = (() => null);
    }

    tableActionItem.eventEmitter.emit(tableActionItem.supplier());
    if (!(tableActionItem.keepSelectedRowOnClick ?? false)) {
      this.selectedRow = null;
    }
  }

  protected isCustomMenuItemEnabled(tableActionItem: TableActionItem): boolean {
    if (tableActionItem.enabled === 'RowSelected') {
      return this.selectedRow !== null;
    } else if (tableActionItem.enabled === 'NoRowSelected') {
      return this.selectedRow === null;
    }
    return true;
  }

  protected toggleInfoPanel(): void;
  protected toggleInfoPanel(visible: boolean): void;
  protected toggleInfoPanel(visible?: boolean): void {
    if (typeof visible === 'boolean') {
      this.showMoreInfoMessage = visible;
      return;
    }
    this.showMoreInfoMessage = !this.showMoreInfoMessage;
  }

}
